import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { colors, typo } from '../../../../theme/tokens';
import { WriteIconButton } from '../../../../components/ui';
import { GroupScope, tenantGroupsApi } from '../../../../api/tenantGroupsApi';
import { useScopeEntities } from '../hooks/useScopeEntities';
import EntityLabel from './EntityLabel';

const SCOPE_LABEL: Record<GroupScope, string> = {
  customer: 'Customer',
  sponsor: 'Sponsor',
  tenant: 'Tenant',
};

/**
 * Read-only view of a user's GROUP-DERIVED access, grouped by source group and
 * clearly badged ("from group"). Rendered inside the drawer's "Current access"
 * area, BELOW the individual grants. Group-derived entries are intentionally NOT
 * individually removable — they clear only when the group is unassigned (see
 * AssignedGroupsSection). A dashed border + subtle fill distinguishes them from
 * the solid-bordered, individually-removable direct grants.
 */
export const GroupDerivedAccessList: React.FC<{ userId: number; open: boolean }> = ({ userId, open }) => {
  const { nameFor } = useScopeEntities(open);
  const userGroupsQuery = useQuery({
    queryKey: ['user-groups', userId],
    queryFn: () => tenantGroupsApi.listUserGroups(userId),
    enabled: open && userId > 0,
  });

  const groups = userGroupsQuery.data ?? [];

  if (userGroupsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }
  if (userGroupsQuery.isError) {
    return (
      <Typography sx={{ ...typo.tinyText, color: colors.errorText, mt: 1.5 }}>
        Couldn&apos;t load group-derived access.
      </Typography>
    );
  }
  if (groups.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.5 }}>
      <Typography sx={{ ...typo.tinyText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Via assigned groups
      </Typography>
      {groups.map((g) => (
        <Box
          key={g.id}
          sx={{ border: `1px dashed ${colors.borderDefault}`, borderRadius: '10px', p: 1.25, backgroundColor: colors.bgSubtle }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: g.members.length ? 1 : 0, flexWrap: 'wrap' }}>
            <GroupWorkOutlinedIcon sx={{ fontSize: 16, color: colors.adminAccent }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{g.name}</Typography>
            <Chip
              size="small"
              label="from group"
              sx={{ height: 18, fontSize: 9.5, fontWeight: 700, backgroundColor: colors.adminAccentSubtle, color: colors.adminAccent }}
            />
            <Chip
              size="small"
              label={g.level}
              color={g.level === 'write' ? 'warning' : 'default'}
              sx={{ height: 18, fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase' }}
            />
          </Box>
          {g.members.length === 0 ? (
            <Typography sx={{ ...typo.tinyText, fontStyle: 'italic' }}>This group has no members.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {g.members.map((m) => (
                <Box key={`${g.id}:${m.scope_type}:${m.scope_code}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                  <Chip size="small" label={SCOPE_LABEL[m.scope_type]} sx={{ height: 18, fontSize: 9.5, fontWeight: 600 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EntityLabel scope={m.scope_type} name={nameFor(m.scope_type, m.scope_code)} code={m.scope_code} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

/**
 * Management section: assign an existing group to the user (a live reference)
 * and list / unassign the groups they hold. Placed below the individual-grant
 * controls in AccessGrantsDrawer. Unassigning here is the ONLY way to remove
 * group-derived access (individual derived rows have no delete control).
 */
const AssignedGroupsSection: React.FC<{
  userId: number;
  open: boolean;
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}> = ({ userId, open, onNotify }) => {
  const qc = useQueryClient();
  const [inputValue, setInputValue] = useState('');

  const allGroupsQuery = useQuery({
    queryKey: ['tenant-groups'],
    queryFn: () => tenantGroupsApi.listGroups(),
    enabled: open,
  });
  const userGroupsQuery = useQuery({
    queryKey: ['user-groups', userId],
    queryFn: () => tenantGroupsApi.listUserGroups(userId),
    enabled: open && userId > 0,
  });

  const assigned = useMemo(() => userGroupsQuery.data ?? [], [userGroupsQuery.data]);
  const assignedIds = useMemo(() => new Set(assigned.map((g) => g.id)), [assigned]);
  const available = (allGroupsQuery.data ?? []).filter((g) => !assignedIds.has(g.id));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['user-groups', userId] });
    // The groups list carries assigned_user_count (the Members column) —
    // assigning/unassigning here changes it, so keep the list live too.
    qc.invalidateQueries({ queryKey: ['tenant-groups'] });
  };

  const assignMut = useMutation({
    mutationFn: (groupId: number) => tenantGroupsApi.assignGroup(userId, groupId),
    onSuccess: () => { refresh(); onNotify('Group assigned.', 'success'); },
    onError: () => onNotify('Failed to assign group.', 'error'),
  });
  const unassignMut = useMutation({
    mutationFn: (groupId: number) => tenantGroupsApi.unassignGroup(userId, groupId),
    onSuccess: () => { refresh(); onNotify('Group unassigned.', 'success'); },
    onError: () => onNotify('Failed to unassign group.', 'error'),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography sx={{ ...typo.fieldLabel, mb: 0.25 }}>Assigned groups</Typography>
        <Typography sx={{ ...typo.tinyText }}>
          Assigning a group grants its members at the group&apos;s level. It&apos;s a live reference —
          if the group&apos;s members change, this user&apos;s access changes too.
        </Typography>
      </Box>

      {/* Assign picker */}
      <Autocomplete
        size="small"
        options={available}
        value={null}
        inputValue={inputValue}
        onInputChange={(_e, v, reason) => { if (reason !== 'reset') setInputValue(v); }}
        loading={allGroupsQuery.isLoading}
        disabled={assignMut.isPending}
        getOptionLabel={(g) => g.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_e, g) => { if (g) { assignMut.mutate(g.id); setInputValue(''); } }}
        noOptionsText={(allGroupsQuery.data ?? []).length === 0 ? 'No groups exist yet' : 'All groups already assigned'}
        renderOption={(props, g) => {
          const { key, ...liProps } = props as { key?: React.Key } & React.HTMLAttributes<HTMLLIElement>;
          return (
            <Box component="li" key={key} {...liProps} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{g.name}</Typography>
              <Chip
                size="small"
                label={g.level}
                color={g.level === 'write' ? 'warning' : 'default'}
                sx={{ height: 18, fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase' }}
              />
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Assign a group…"
            InputProps={{ ...params.InputProps, sx: { borderRadius: '10px' } }}
          />
        )}
      />

      {/* Assigned list */}
      {userGroupsQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}><CircularProgress size={18} /></Box>
      ) : userGroupsQuery.isError ? (
        <Typography sx={{ fontSize: 13, color: colors.errorText }}>
          Couldn&apos;t load assigned groups — try again.
        </Typography>
      ) : assigned.length === 0 ? (
        <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: colors.textTertiary }}>
          No groups assigned.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {assigned.map((g) => (
            <Box
              key={g.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, border: `1px solid ${colors.borderLight}`, borderRadius: '10px' }}
            >
              <GroupWorkOutlinedIcon sx={{ fontSize: 18, color: colors.adminAccent }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {g.name}
                </Typography>
                <Typography sx={{ ...typo.tinyText }}>
                  {g.members.length} member{g.members.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={g.level}
                color={g.level === 'write' ? 'warning' : 'default'}
                sx={{ height: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}
              />
              <Tooltip title="Unassign group">
                <span>
                  <WriteIconButton
                    ignoreEnvReadOnly
                    size="small"
                    aria-label={`Unassign group — ${g.name}`}
                    disabled={unassignMut.isPending}
                    onClick={() => unassignMut.mutate(g.id)}
                  >
                    <LinkOffIcon sx={{ fontSize: 18, color: colors.errorIcon }} />
                  </WriteIconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AssignedGroupsSection;
